import { useContext, useEffect, useMemo, useState } from "react"
import services from "../../../services/services"
import { Urls } from "../../../services/Urls"
import { callautopopulate } from "../../../services/callautopop";
import { ToastContext } from "../../../context/ToastContext";
import _ from "underscore";
import { groupByTwoKeys } from "../../../utils/Helper";

export const useAwardNominations = ({ filterValues, setFilterValues, years }) => {

    const { showSuccess, showInfo, showError } = useContext(ToastContext);
    const [options, setOptions] = useState({ users: [], category: [], year: [] })
    const [createNominee, setCreateNominee] = useState({ visible: false });
    const [loader, setLoader] = useState({ btn: false });
    const [nominations, setNominations] = useState([])
    const nomineeFields = [
        {
            label: "Year",
            name: "year_id",
            col: "12",
            type: "select",
            option: years,
            optionLabel: "year_name",
            optionValue: "year_id",
            required: true,
            disabled: createNominee?.year_id
        },
        {
            label: "Category",
            name: "category_id",
            col: "12",
            type: "select",
            option: filterValues?.categoryList,
            optionLabel: "category_name",
            optionValue: "category_id",
            required: true,
        },
        {
            label: "Nominee",
            name: "nominee_user_code",
            col: "12",
            type: "multiselect",
            option: options?.users,
            optionLabel: "user_display_name",
            optionValue: "user_code",
            required: true,
            disabled: createNominee?.user_code
        },
    ]

    useEffect(() => {
        callautopopulate("users")?.then(res => {
            setOptions((prev) => ({ ...prev, users: res?.users?.sort((a, b) => a?.user_display_name?.localeCompare(b?.user_display_name)) }));
        })
    }, [])

    useEffect(() => {
        filterValues?.nomineeApiCall && getNominees();
    }, [filterValues?.year, filterValues?.nomineeApiCall])

    const getNominees = async () => {
        try {
            const payload = {
                year_id: filterValues?.year?.year_id || 0,
                category_id: filterValues?.category?.category_id || 0,
            }
            const res = await services.getInvokeRemote("POST", Urls("GETAWARDNOMINATIONS"), payload);
            if (res?.body?.error_status === false) {
                const data = res?.body?.data?.award_nominations
                const sortedData = data.sort((a, b) => {
                    if (a?.category_name < b?.category_name) return 1;
                    if (a?.category_name > b?.category_name) return -1;
                    return a?.user_display_name.localeCompare(b?.user_display_name);
                });
                setNominations(_.sortBy(sortedData, "year_name")?.reverse());
            }
        } catch (error) {
            console.error("Error: ", error);
        } finally {
            setFilterValues((prev) => ({ ...prev, nomineeApiCall: false }));
        }
    }

    const saveNominees = async () => {
        try {
            if (!createNominee?.category_id) {
                showInfo("Info", "Please select the category");
                return;
            } else if (_.isEmpty(createNominee?.nominee_user_code)) {
                showInfo("Info", "Please select the nominee");
                return;
            } else if (!createNominee?.isChange) {
                showInfo("Info", "No changes made");
                return;
            }
            setLoader((prev) => ({ ...prev, btn: true }));
            const payload = {
                nomination_id: createNominee?.nomination_id || 0,
                year_id: filterValues?.year?.year_id || 0,
                category_id: createNominee?.category_id || 0,
                nominee_user_codes: createNominee?.nominee_user_code?.toString() || 0,
                is_nominated: true
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTAWARDNOMINATIONS"), payload);
            if (res?.body?.error_status === false) {
                showSuccess("Success", res?.body?.message);
                setCreateNominee({ visible: false });
                getNominees();
            }
        } catch (error) {
            console.error("Error: ", error);
            error?.response?.data?.errorMessage && showError("Error", error?.response?.data?.errorMessage);
        } finally {
            setLoader((prev) => ({ ...prev, btn: false }));
            setFilterValues((prev) => ({ ...prev, nomineeApiCall: false }));
        }
    }

    const deleteNominee = async () => {
        try {
            setLoader((prev) => ({ ...prev, delete: true }));
            const payload = {
                nomination_id: createNominee?.delete?.nominee?.nomination_id || 0,
            }
            const res = await services.getInvokeRemote("POST", Urls("DELETEAWARDNOMINATIONS"), payload);
            if (res?.body?.error_status === false) {
                showSuccess("Success", res?.body?.message);
                setNominations((prev) => [...prev?.filter(item => item?.nomination_id !== createNominee?.delete?.nominee?.nomination_id)]);
                setFilterValues((prev) => ({ ...prev, categoryList: prev?.categoryList?.filter(item => item?.nomination_id !== createNominee?.delete?.nominee?.nomination_id) }));
                setCreateNominee({ visible: false });
            }
        } catch (error) {
            console.error("Error: ", error);
            error?.response?.data?.errorMessage && showError("Error", error?.response?.data?.errorMessage);
        } finally {
            setLoader((prev) => ({ ...prev, delete: false }));
        }
    }

    const declareWinner = async (isWinner, data) => {
        try {
            const payload = {
                nomination_id: data?.nomination_id || 0,
                is_winner: isWinner
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTAWARDNOMINATIONWINNER"), payload);
            if (res?.body?.error_status === false) {
                showSuccess("Success", res?.body?.message);
                getNominees();
            }
        } catch (error) {
            console.error("Error: ", error);
            error?.response?.data?.errorMessage && showError("Error", error?.response?.data?.errorMessage);
        }
    }

    const filteredNominess = useMemo(() => {
        if (!filterValues?.search?.trim()) return groupByTwoKeys(nominations || [], "year_name", "category_name");
        const filtered = nominations?.filter(item => item?.user_display_name?.toLowerCase()?.includes(filterValues?.search));
        const grouped = groupByTwoKeys(filtered, "year_name", "category_name");
        return grouped;
    }, [filterValues?.search, nominations])

    return { nominations: filteredNominess, createNominee, setCreateNominee, loader, nomineeFields, saveNominees, deleteNominee, declareWinner }
}